import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React, {useState} from 'react';

import {
  BulkArchiveFilesOptions,
  bulkArchiveTemplateCollections,
  bulkUnarchiveTemplateCollections,
} from 'archive/ArchiveActions';
import {getArchiveAction, getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import {ArchivableItem, NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {trackFormArchive, trackFormUnarchive} from 'forms/FormsAnalytics';
import {ActionOrigin, LimitOffsetInput, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  TemplateCollectionPageListingQuery,
  TemplateCollectionPageListingQueryVariables,
} from 'notebook/entry-templates/collections/__generated/TemplateCollectionPageListingQuery';
import {TemplateCollectionPageSummaryQuery} from 'notebook/entry-templates/collections/__generated/TemplateCollectionPageSummaryQuery';
import ManageTemplateCollectionModal from 'notebook/entry-templates/collections/ManageTemplateCollectionModal';
import {TYPE_TO_LABEL} from 'notebook/entry-templates/collections/TemplateCollectionPage';
import TemplateCollectibleCreateButton from 'notebook/template-collections/TemplateCollectibleCreateButton';
import TemplateCollectionUtil, {
  ASSAY,
  ANALYSIS_TEMPLATE,
  FORM_DEFINITION,
  PLATE_DESIGN_TEMPLATE,
  RECIPE,
  REQUEST_V2_DEFINITION,
  SUBTEMPLATE,
  TEMPLATE,
} from 'notebook/template-collections/TemplateCollectionUtil';
import {getProcedureIcon} from 'procedures/design/procedureIconUtils';
import {FONT_PROXIMA_11, FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import PublishingStatusRenderer from 'template-collectibles/publishing-ui/ListingRenderers/PublishingStatusRenderer';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import MultiSelectSearchableDropdown from 'ui-library/dropdowns/MultiSelectSearchableDropdown';
import EditableItemTable, {EditableItemRowProps} from 'ui-library/EditableItemTable';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import PlainSearchInput, {BorderRadius} from 'ui-library/PlainSearchInput';
import ResultsPager from 'ui-library/ResultsPager';
import WarningBadge from 'ui-library/WarningBadge';
import APIUtil, {apiIdToIconName} from 'util/APIUtil';
import {readableTime} from 'util/DateUtil';
import {typeToName} from 'util/NameUtil';
import {assertNever} from 'util/Types';

import {CollectibleListingGraphQL} from '../../../../graphql-fragments/notebook/templates/__generated/CollectibleListingGraphQL';

export type CollectionPageTemplateCollectionNode = ExtractByTypename<
  TemplateCollectionPageSummaryQuery['templateCollection'],
  'TemplateCollectionNode'
>;

export type ListingTemplateCollectionNode = ExtractByTypename<
  TemplateCollectionPageListingQuery['templateCollection'],
  'TemplateCollectionNode'
>;

export type TemplateCollectible = ExtractByTypename<
  ListingTemplateCollectionNode['collectiblesListing'],
  'TemplateCollectiblesListingNode'
>['collectibles'][number];

const LONG_COL_WIDTH = 4;
const COLUMN_WIDTH = 2;
const EMPTY_SEARCH_QUERY = '';
const ALL_ITEMS = 'All items';

export const TYPE_TO_NAME_FOR_FILTER = new Map<string, string>([
  [ModelTypeConstants.TEMPLATE, TEMPLATE],
  [ModelTypeConstants.SUBTEMPLATE, SUBTEMPLATE],
  [ModelTypeConstants.FORM_DEFINITION, FORM_DEFINITION],
  [ModelTypeConstants.REQUEST_V2_DEFINITION, REQUEST_V2_DEFINITION],
  // For assays and recipes, the filterValue is different from the ModelTypeConstants value
  [typeToName(ModelTypeConstants.ASSAY_PROCEDURE), ASSAY],
  [typeToName(ModelTypeConstants.RECIPE_PROCEDURE), RECIPE],
  [ModelTypeConstants.ANALYSIS_TEMPLATE, ANALYSIS_TEMPLATE],
  [ModelTypeConstants.PLATE_DESIGN_TEMPLATE, PLATE_DESIGN_TEMPLATE],
]);

export const TEMPLATE_COLLECTION_PAGE_LISTING_QUERY = gql`
  query TemplateCollectionPageListingQuery(
    $collectionId: StrictString!
    $searchInput: StrictString
    $typeFilter: [StrictString!]
    $archiveFilter: [StrictString]
    $limitOffsetInput: LimitOffsetInput
  ) @skipAudit {
    templateCollection(id: $collectionId) {
      ... on TemplateCollectionNode {
        id
        collectiblesListing(
          searchInput: $searchInput
          typeFilter: $typeFilter
          archiveFilter: $archiveFilter
          limitOffsetInput: $limitOffsetInput
        ) {
          total
          collectibles {
            ... on TemplateCollectibleInterface {
              id
              name
              archiveRecord {
                purpose
              }
              editUrl
              creator {
                id
                name
                avatarUrl
              }
              createdAt
              modifiedAt
              templateCollection {
                id
                name
                url
                counts
                description
                createdAt
                modifiedAt
              }
            }
            ...CoreObjectArchivableGraphQL
            ... on FormDefinitionNode {
              id
              isValid
            }
            ... on ProcedureNode {
              id
              procedureType
            }
            ...PublishingStatusForListingPageGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('CoreObjectArchivable').fragment}
  ${getFragmentWrapper('PublishingStatusForListingPage').fragment}
`;

function getColumnHeaders(): Array<{name: string; width: number}> {
  if (BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING) {
    return [
      {name: 'Name', width: LONG_COL_WIDTH},
      {name: 'Publishing Status', width: COLUMN_WIDTH},
      {name: 'Author', width: COLUMN_WIDTH},
      {name: 'Created', width: COLUMN_WIDTH},
      {name: 'Last Modified', width: COLUMN_WIDTH},
    ];
  }

  return [
    {name: 'Name', width: LONG_COL_WIDTH},
    {name: 'Type', width: COLUMN_WIDTH},
    {name: 'Author', width: COLUMN_WIDTH},
    {name: 'Created', width: COLUMN_WIDTH},
    {name: 'Last Modified', width: COLUMN_WIDTH},
  ];
}

/**
 * Returns template collectible type for use in TYPE_TO_LABEL.
 * Use instead of calling apiIdToType directly if you want to distinguish between recipes and assays.
 */
export function getTemplateCollectibleType(
  templateCollectibleInfo: TemplateCollectible | CollectibleListingGraphQL
): string {
  if (templateCollectibleInfo.__typename === 'ProcedureNode') {
    const procedureType = templateCollectibleInfo.procedureType;
    switch (procedureType) {
      case ProcedureType.RECIPE:
        return ModelTypeConstants.RECIPE_PROCEDURE;
      case ProcedureType.ASSAY:
        return ModelTypeConstants.ASSAY_PROCEDURE;
      default:
        assert(false, `Unexpected procedure type: ${assertNever(procedureType)}`);
    }
  }
  return APIUtil.apiIdToType(templateCollectibleInfo.id);
}

function mapTemplateToRowValueForPublishing(tmpt: TemplateCollectible): Array<JSX.Element | string> {
  const isPublishingSupportedForItemType =
    tmpt.__typename === 'TemplateNode' ||
    tmpt.__typename === 'SubtemplateNode' ||
    (tmpt.__typename === 'ProcedureNode' && BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING);
  const currentPublishingRecord = isPublishingSupportedForItemType ? tmpt.currentPublishingRecord : null;
  if (currentPublishingRecord) {
    assert(!isInaccessible(currentPublishingRecord), 'Expected current publishing record to be accessible.');
  }
  const publishingStatusCell = (
    <PublishingStatusRenderer
      isPublishingSupportedForItemType={isPublishingSupportedForItemType}
      currentPublishingRecord={currentPublishingRecord}
    />
  );

  return [
    <TemplateCollectibleNameCell
      templateCollectibleInfo={tmpt}
      markNeedsUpdate={tmpt.__typename === 'FormDefinitionNode' && !tmpt.isValid}
    />,
    publishingStatusCell,
    <span>
      <Avatar entity={tmpt.creator} size={AvatarSize.XXSMALL} />
      &nbsp;{tmpt.creator.name}
    </span>,
    readableTime(tmpt.createdAt, {showTime: false}),
    readableTime(tmpt.modifiedAt, {showTime: false}),
  ];
}

function mapTemplateToRowValue(tmpt: TemplateCollectible): Array<JSX.Element | string> {
  if (BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING) {
    return mapTemplateToRowValueForPublishing(tmpt);
  }

  return [
    <TemplateCollectibleNameCell
      templateCollectibleInfo={tmpt}
      markNeedsUpdate={tmpt.__typename === 'FormDefinitionNode' && !tmpt.isValid}
    />,
    TYPE_TO_LABEL.get(getTemplateCollectibleType(tmpt))?.() || '',
    tmpt.creator.name,
    readableTime(tmpt.createdAt, {showTime: false}),
    readableTime(tmpt.modifiedAt, {showTime: false}),
  ];
}

function onTemplateArchive(
  templates: Array<ArchivableItem & TemplateCollectible>,
  refetch: () => void
): void {
  const filteredTemplates = templates.filter((template) => !isArchived(template));

  ArchiveModal.open({
    items: filteredTemplates,
    onArchive: (templateIds: Array<string>, purpose: string, options: BulkArchiveFilesOptions): void => {
      getArchiveAction(templateIds)(templateIds, purpose, options, () => {
        refetch();
        trackFormArchive(templateIds, {
          action_origin: ActionOrigin.NOTEBOOK_TEMPLATE_COLLECTION_PAGE,
        });
      });
    },
  });
}

function onTemplateUnarchive(
  templates: Array<ArchivableItem & TemplateCollectible>,
  refetch: () => void
): void {
  const filteredTemplates = templates.filter((template) => template.archiveRecord);
  const templateIds = filteredTemplates.map((template) => template.id);
  getUnarchiveAction(templateIds)(templateIds, undefined, () => {
    refetch();
    trackFormUnarchive(templateIds, {action_origin: ActionOrigin.NOTEBOOK_TEMPLATE_COLLECTION_PAGE});
  });
}

const customEmptyComponent = (): JSX.Element => (
  <div className={css(styles.emptyComponent, styles.centerColumnHorizontally, styles.centerVertically)}>
    <BenchlingIcon className={css(styles.icon, styles.darkGray)} icon="benchling" />
    <div className={css(styles.headerDescription)}>Nothing here!</div>
  </div>
);

interface TemplateCollectibleNameCellProps {
  templateCollectibleInfo: Pick<TemplateCollectible, 'id' | 'name' | 'archiveRecord' | '__typename'> & {
    procedureType?: ProcedureType;
  };
  markNeedsUpdate?: boolean;
}

export function TemplateCollectibleNameCell({
  templateCollectibleInfo,
  markNeedsUpdate = false,
}: TemplateCollectibleNameCellProps): JSX.Element {
  // Specific types for procedures don't matter here because this string is only used for forms
  const templateCollectibleType = TYPE_TO_LABEL.get(APIUtil.apiIdToType(templateCollectibleInfo.id))?.();
  const text = templateCollectibleInfo.archiveRecord
    ? `${templateCollectibleInfo.name} (${templateCollectibleInfo.archiveRecord.purpose})`
    : templateCollectibleInfo.name;
  return (
    <div>
      {markNeedsUpdate && (
        <WarningBadge
          content={null}
          tooltipTitle={`Invalid ${templateCollectibleType || 'item'} needs review`}
        />
      )}
      <BenchlingIcon
        className="typeIcon u-small-spacing-left"
        icon={
          templateCollectibleInfo.procedureType != null
            ? getProcedureIcon(templateCollectibleInfo.procedureType)
            : apiIdToIconName(templateCollectibleInfo.id)
        }
      />
      <span className="u-small-spacing-left">{text}</span>
    </div>
  );
}

export interface TemplateCollectionTableProps {
  collection: CollectionPageTemplateCollectionNode;
  resultType: QueryResultType;
  summaryRefetch: () => void;
}

export default function TemplateCollectionTable({
  collection,
  resultType,
  summaryRefetch,
}: TemplateCollectionTableProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState(EMPTY_SEARCH_QUERY);
  const debouncedSearchQuery = useDebounce(searchQuery, 250);
  const [templateFilters, setTemplateFilters] = useState<Array<string>>([ALL_ITEMS]);
  const [limitOffsetInput, setLimitOffsetInput] = useState<LimitOffsetInput>({limit: 200, offset: 0});
  const [archivePurposeFilters, setArchivePurposeFilters] = useState([NOT_ARCHIVED_PURPOSE_STRING]);

  let filteredCollection: Array<TemplateCollectible> = [];
  let total = -1;
  let loadingNextPage = false;
  const listingResult = useSkippableBenchlingQuery<
    TemplateCollectionPageListingQuery,
    TemplateCollectionPageListingQueryVariables
  >(TEMPLATE_COLLECTION_PAGE_LISTING_QUERY, {
    variables: {
      collectionId: collection.id,
      searchInput: debouncedSearchQuery,
      typeFilter:
        templateFilters.includes(ALL_ITEMS) || templateFilters.length === 0 ? undefined : templateFilters,
      archiveFilter: archivePurposeFilters.map((filter) =>
        filter === NOT_ARCHIVED_PURPOSE_STRING ? null : filter
      ),
      limitOffsetInput,
    },
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
  });

  if (listingResult.type === QueryResultType.ERROR) {
    return <div>Cannot load items for collection</div>;
  } else if (listingResult.type === QueryResultType.SUCCESS) {
    const collection = listingResult.data.templateCollection;
    assert(!isInaccessible(collection), 'Expected result schema to be accessible.');
    // Since we render cached data, we have to show loading when the collectibles are not present.
    // The GQL query will initially return success with only the collection available with cache-and-network
    loadingNextPage = listingResult.loading && collection.collectiblesListing === undefined;
    filteredCollection = collection.collectiblesListing ? collection.collectiblesListing.collectibles : [];
    total = collection.collectiblesListing ? collection.collectiblesListing.total : -1;
  }

  function showSettingsClickHandler(): void {
    legacyRunModal(ManageTemplateCollectionModal, {
      templateCollection: collection,
      onFilterViewToProceduresOnly: () => {
        const procedureFilters: Array<string> = [];
        if ((collection.counts[ModelTypeConstants.RECIPE_PROCEDURE] ?? 0) > 0) {
          procedureFilters.push(typeToName(ModelTypeConstants.RECIPE_PROCEDURE));
        }
        if ((collection.counts[ModelTypeConstants.ASSAY_PROCEDURE] ?? 0) > 0) {
          procedureFilters.push(typeToName(ModelTypeConstants.ASSAY_PROCEDURE));
        }
        setTemplateFilters(procedureFilters);
      },
    });
  }

  const templateTypes = TemplateCollectionUtil.getTemplateCollectionTypes(collection);

  const menuOptions = [
    {name: ALL_ITEMS, value: ALL_ITEMS},
    ...templateTypes
      .filter((templateType) => templateType.isViewable())
      .map(({name, filterValue}) => ({name: pluralize(2, name), value: filterValue})),
  ];

  function handleTemplateFilterSelect(selectedValues: Array<string>): void {
    const wasAllItemsSelected = templateFilters.includes(ALL_ITEMS) || templateFilters.length === 0;
    const isAllItemsNowSelected = selectedValues.includes(ALL_ITEMS);

    if (isAllItemsNowSelected && !wasAllItemsSelected) {
      // User just selected "All items" — clear specific filters
      setTemplateFilters([ALL_ITEMS]);
    } else if (isAllItemsNowSelected && selectedValues.length > 1) {
      // User selected a specific type while "All items" was active — remove "All items"
      setTemplateFilters(selectedValues.filter((v) => v !== ALL_ITEMS));
    } else {
      setTemplateFilters(selectedValues);
    }
  }

  const rows: Array<EditableItemRowProps> =
    resultType === QueryResultType.SUCCESS && collection
      ? filteredCollection.map((tmpt, itemIndex) => {
          const row: EditableItemRowProps = {
            id: tmpt.id,
            itemIndex,
            values: mapTemplateToRowValue(tmpt),
            isArchivable: true,
            isArchived: isArchived(tmpt),
          };
          return row;
        })
      : [];

  const getItemUrl = (itemIndex: number): string => {
    if (collection == null) {
      return '';
    }
    return filteredCollection[itemIndex].editUrl;
  };

  const loading =
    resultType === QueryResultType.PENDING ||
    resultType === QueryResultType.SKIPPED ||
    loadingNextPage ||
    listingResult.type === QueryResultType.PENDING;
  const buttonEnabledCondition = collection?.permissions?.appendable;
  const buttonDisabled = !buttonEnabledCondition || loading;

  // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
  function ButtonBar(): JSX.Element {
    return (
      <div className={css(styles.buttonToolbar)}>
        <ExportAuditLogButton
          ids={[collection.id]}
          buttonSize={ButtonSize.SMALL}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
        />
        {BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING && (
          <ArchiveOrUnarchiveButton
            isArchivable={Boolean(collection.permissions.archivable)}
            isUnarchivable={Boolean(collection.permissions.unarchivable)}
            item={{
              id: collection.id,
              name: collection.name,
              archiveRecord: collection.archiveRecord,
            }}
            onArchive={(ids: Array<string>, purpose: string, options: BulkArchiveFilesOptions) => {
              bulkArchiveTemplateCollections(ids, purpose, options, () => {
                summaryRefetch();
                listingResult.refetch();
              });
            }}
            onUnarchive={(ids: Array<string>) => {
              bulkUnarchiveTemplateCollections(ids, undefined, () => {
                summaryRefetch();
                listingResult.refetch();
              });
            }}
          />
        )}
        <Tooltip placement={FloatingPlacement.TOP} text="Settings">
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            icon="settings"
            onClick={showSettingsClickHandler}
            size={ButtonSize.SMALL}
          />
        </Tooltip>

        <TemplateCollectibleCreateButton collectionId={collection.id} disabled={buttonDisabled} />
      </div>
    );
  }

  return (
    <div>
      <EditableItemTable
        columnHeaders={getColumnHeaders()}
        emptyMessage={customEmptyComponent()}
        isLoading={loading}
        rows={rows}
        getEditUrl={getItemUrl}
        writable={true}
        onArchive={
          BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING
            ? (itemIndex: number) => {
                onTemplateArchive([filteredCollection[itemIndex]], listingResult.refetch);
              }
            : null
        }
        onUnarchive={
          BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING
            ? (itemIndex: number) => {
                onTemplateUnarchive([filteredCollection[itemIndex]], listingResult.refetch);
              }
            : null
        }
        leftSubheaderNode={
          <div>
            <div className="u-flex-display">
              <PlainSearchInput
                className={css(styles.searchInput)}
                borderRadius={BorderRadius.LARGE}
                placeholder="Search"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  // reset to the first page of results when the search is updated
                  setLimitOffsetInput((previousLimitOffset) => ({...previousLimitOffset, offset: 0}));
                  setSearchQuery(e.target.value);
                }}
                onEscape={() => {
                  // reset to the first page of results when the search is updated
                  setLimitOffsetInput((previousLimitOffset) => ({...previousLimitOffset, offset: 0}));
                  setSearchQuery(EMPTY_SEARCH_QUERY);
                }}
              />
              <MultiSelectSearchableDropdown
                className={css(styles.filterDropdown)}
                dataTestAttrs={{component: 'TemplateCollectionTable', element: 'typeFilter'}}
                noneSelectedMessage={ALL_ITEMS}
                onSelect={handleTemplateFilterSelect}
                optionDatas={menuOptions}
                selectedValues={templateFilters}
                menuProps={{width: MenuWidth.MEDIUM}}
              />
              {BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING && (
                <ArchivePurposeDropdown
                  // Keep list of ids static to combat dropdown text changing as data loads from graphql
                  itemIds={['tmpl_123', 'sbtmpl_123', 'fmdef_123']}
                  onChangePurposes={setArchivePurposeFilters}
                  selectedPurposes={archivePurposeFilters}
                />
              )}
            </div>
            <ResultsPager
              allowFastForwardAndBackward={true}
              isLoading={loading}
              setOffset={(offset: number) => {
                setLimitOffsetInput((prevLimitOffset) => ({...prevLimitOffset, offset}));
              }}
              pagination={{...limitOffsetInput, total}}
            />
          </div>
        }
        rightSubheaderNode={<ButtonBar />}
      />
      {loading || total < limitOffsetInput.limit ? null : (
        <ResultsPager
          allowFastForwardAndBackward={true}
          isLoading={loading}
          setOffset={(offset: number) => {
            setLimitOffsetInput((prevLimitOffset) => ({...prevLimitOffset, offset}));
          }}
          pagination={{...limitOffsetInput, total}}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  searchInput: {
    marginRight: 10,
    width: 200,
  },
  filterDropdown: {
    marginRight: 10,
  },
  centerVertically: {
    display: 'flex',
    alignItems: 'center',
  },
  learnMoreText: {
    fontSize: '7px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  learnMoreButton: {
    padding: '0px 4px',
    marginLeft: '10px',
    height: '14px',
  },
  centerColumnHorizontally: {
    height: '70vh',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  emptyComponent: {
    paddingLeft: '30%',
    paddingRight: '30%',
  },
  headerDescription: {
    margin: '0px',
    color: NEAR_BLACK,
    ...FONT_PROXIMA_13,
  },
  darkGray: {
    color: GRAY_DARKER,
  },
  subDescription: {
    margin: '20px',
  },
  reviewDescription: {
    ...FONT_PROXIMA_11,
  },
  icon: {
    margin: '20px',
    width: '69px',
    height: '69px',
  },
  resultsPager: {
    paddingLeft: '20px',
  },
  buttonToolbar: {
    display: 'flex',
    flexDirection: 'row',
    gap: '6px',
  },
});
